import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { forkJoin, Subscription } from 'rxjs';
import { COMMERCIAL_CONTACT_DETAIL_CONTAINER } from 'src/app/constants/common.constants';
import { NEW_COMMERCIAL_CONTACT } from 'src/app/constants/commercial-constants';
import {
  LazyLoadFilters,
  PageRequest,
  SearchContainerComponent,
} from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { CommercialContact } from 'src/app/model/commercial-services/commercial-contact';
import { CommercialContactApiService } from 'src/app/services/api/commercial-contact-api.service';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { CommercialContactBuilderService } from 'src/app/services/builder/commercial-contact-builder.service';
import { CommercialContactTableService } from 'src/app/services/table/commercial-contact-table.service';
import { getCommercialContactTableColumns } from '../../definitions/commercial-contacts-table-definition';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { SelectItem } from '@openng/optimus-ui/api';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { AdminApiService } from 'src/app/services/api/admin-api.service';

/**
 * Main search component for the Commercial Services Contacts module.
 */
@Component({
  selector: 'app-commercial-contacts-search-container',
  templateUrl: './commercial-contacts-search-container.component.html',
  styleUrls: ['./commercial-contacts-search-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BlockUI,
    ButtonDirective,
    ProgressSpinnerComponent,
    GeneralGridComponent,
    TranslatePipe,
  ],
})
export class CommercialContactsSearchContainerComponent
  extends SearchContainerComponent<CommercialContact>
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbBuilderService = inject(BreadCrumbBuilderService);
  protected _commercialContactApi = inject(CommercialContactApiService);
  protected _commercialContactBuilder = inject(CommercialContactBuilderService);
  protected _commercialContactTableService = inject(
    CommercialContactTableService
  );
  protected _authApi = inject(AuthApiService);
  protected _tabService = inject(TabService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _adminApi = inject(AdminApiService);
  protected _cdRef = inject(ChangeDetectorRef);

  availableCompanies: SelectItem[] = [];
  availableJobRoles: SelectItem[] = [];
  scrollHeight = 'calc(100vh - 256px)';
  activeTabChangedSub?: Subscription;

  ngOnInit(): void {
    this.setTableService(this._commercialContactTableService);
    this.setupActiveTabChangedSubscription();
    this.initialize();
  }

  ngOnDestroy(): void {
    this.activeTabChangedSub?.unsubscribe();
  }

  private setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (tab?.header === this.screenName) {
          this.refresh();
        }
      }
    );
  }

  initialize(): void {
    if (this._deviceService.isMobile()) {
      this.scrollHeight = 'calc(100vh - 180px)';
    }

    this._translateService
      .get('COMMERCIAL_SERVICES.SCREEN.COMMERCIAL_CONTACTS')
      .subscribe((label) => {
        if (this._deviceService.isMobile()) {
          this._breadCrumbBuilderService.resetAndAddBreadCrumb(
            new BreadCrumb(label, null, false)
          );
        } else {
          this._tabService.updateActiveTabLabel(label);
        }
        this.screenName = label;
        this._cdRef.markForCheck();
      });

    forkJoin({
      companies: this._adminApi.getCompanies(),
      jobRoles: this._commercialContactApi.getCommercialJobRoles(),
    }).subscribe(({ companies, jobRoles }) => {
      this.availableCompanies = [
        { label: '', value: null },
        ...companies.map((c) => ({ label: c.name, value: c.id })),
      ];
      this.availableJobRoles = [
        { label: '', value: null },
        ...jobRoles.map((r) => ({ label: r.name, value: r.id })),
      ];
      this.columns = [
        ...getCommercialContactTableColumns(
          this.availableCompanies,
          this.availableJobRoles,
          this._deviceService.isMobile()
        ),
      ];
      this.applyPreviousFilters();
      this.applyPreviousData();
      this._cdRef.markForCheck();
    });
  }

  /**
   * Method called when a row double click event is fired.  This navigates to the details page.
   * @param $event - Event from the double click event.
   */
  changeViewEvent($event: { data: CommercialContact }): void {
    const tab = this._tabService.buildNewTab(
      COMMERCIAL_CONTACT_DETAIL_CONTAINER,
      true,
      undefined,
      $event.data.id
    );
    if (this._deviceService.isMobile()) {
      this._tabService.setMobileTab(tab);
    } else {
      this._tabService.openTab(tab);
    }
  }

  private queryNetwork(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    query: { [key: string]: any }
  ): void {
    req.page += 1;
    const sort = this.buildNetworkSortQuery($event, 'firstName');

    this._commercialContactApi
      .queryForCommercialContacts(req.pageSize, req.page, query, sort)
      .subscribe({
        next: (data) => {
          this.elements = [...data.items];
          this.finalizeQuery(data.totalRecords);
          this._cdRef.markForCheck();
        },
        error: (error) => {
          console.error(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            bodyKey:
              'COMMERCIAL_SERVICES.MESSAGES.ERROR.LOAD_COMMERCIAL_CONTACTS',
          });
          this.loading = false;
          this._cdRef.markForCheck();
        },
      });
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ): void {
    const query =
      this._commercialContactBuilder.buildCommercialContactQuery(filters);
    this.queryNetwork(req, $event, query);
  }

  canUserAddCommercialContact(): boolean {
    return this._authApi.doesUserHaveClaim(
      CLAIMS.COMMERCIAL_SERVICES.COMMERCIAL_CONTACTS.CREATE_COMMERCIAL_CONTACTS
    );
  }

  addNewCommercialContact(): void {
    this._commercialContactTableService.setSelected(null);
    const tab = this._tabService.buildNewTab(
      COMMERCIAL_CONTACT_DETAIL_CONTAINER,
      true,
      undefined,
      NEW_COMMERCIAL_CONTACT
    );
    this._tabService.openTab(tab);
  }

  refresh(): void {
    this.lazyLoad({
      lazy: this._commercialContactTableService.getLastLazyLoad()!,
    });
  }
}
